
import { useEffect, useState } from "react"
import { useParams, Link, useNavigate } from "react-router-dom"
import { Form, FormField, FormGroup, Input, Button, Dropdown, TextArea, Icon, Message } from "semantic-ui-react"
import { fetchWithAuth, getCSRFToken, getUsername, isAssociateManagerOrAbove, canApproveTeam, isReadOnly } from "../auth"
import { LoadingWrapper } from "../common"
import { getMemberDetails } from "../common/apiClient"
import { statusMap } from "../common/helpers"
import './Events.css'

import { eventTypes } from "../members/MemberSchedule"
import { Unauthorized } from "../common/Unauthorized"

function toISOStringWithTimezone(date) {
    const tzoffset = (new Date()).getTimezoneOffset() * 60000; //offset in milliseconds
    const localISOTime = (new Date(date - tzoffset)).toISOString().slice(0, -1)
    return localISOTime
}

export const EventDetail = props => {
    const { id } = useParams()
    const navigate = useNavigate()
    const [event, setEvent] = useState({})
    const [initialEvent, setInitialEvent] = useState({})
    const [loading, setLoading] = useState(false)
    const [denialReasonError, setDenialReasonError] = useState(null)
    const [conflictingEvents, setConflictingEvents] = useState([])
    const [notFoundError, setNotFoundError] = useState(false)
    const [genericError, setGenericError] = useState(false)
    const [memberTeams, setMemberTeams] = useState([])
    const [approvalError, setApprovalError] = useState('')

    async function approveEvent() {
        await updateApproval({ approvalStatus: 'approved' })
    }

    async function denyEvent() {
        if (!event.deniedReason) {
            setDenialReasonError("Please explain why this absence was denied.")
            return
        }
        await updateApproval({ approvalStatus: 'denied', deniedReason: event.deniedReason })
    }

    async function updateApproval(payload) {
        if (!canApprove || loading) return
        setLoading(true)
        setApprovalError('')
        try {
            const response = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'events/' + id + '/', {
                headers: { 'X-CSRFToken': getCSRFToken(), 'Content-Type': 'application/json' },
                method: 'PATCH',
                body: JSON.stringify(payload),
            })
            if (!response.ok) {
                const errors = await response.json().catch(() => ({}))
                setApprovalError(response.status === 403 ? 'You do not have permission to approve this absence.'
                    : Object.values(errors).flat().join(' ') || 'Unable to update this absence. Please try again.')
                return
            }
            navigate('/events')
        } catch {
            setApprovalError('Unable to update this absence. Check your connection and try again.')
        } finally {
            setLoading(false)
        }
    }

    async function getEvent() {
        setLoading(true)
        setMemberTeams([])
        const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + 'events/' + id + '/', {
            headers: { 'X-CSRFToken': getCSRFToken() },
            credentials: 'include',
        })
        if (!resp.ok) {
            if (resp.status === 404) {
                setNotFoundError(true)
                setLoading(false)
                return
            } else {
                setGenericError(true)
                setLoading(false)
                return
            }
        }
        const respJson = await resp.json()
        respJson['start'] = toISOStringWithTimezone(new Date(respJson['start']))
        respJson['end'] = toISOStringWithTimezone(new Date(respJson['end']))
        setEvent(respJson)
        setInitialEvent(respJson)

        setLoading(false)
    }

    function handleChange(key, value) {
        setDenialReasonError(null)
        const eventClone = structuredClone(event)
        eventClone[key] = value
        setEvent(eventClone)
    }

    async function getConflictingEvents(teamId) {
        const eventStart = new Date(event.start).toISOString()
        const eventEnd = new Date(event.end).toISOString()
        const resp = await fetchWithAuth(process.env.REACT_APP_ICC_TOOL_API_HOST + `events?team=${teamId}&start=${eventStart}&end=${eventEnd}`, {
            headers: { 'X-CSRFToken': getCSRFToken() }
        })
        const respJson = await resp.json()
        setConflictingEvents(respJson.filter((conflictingEvent) => conflictingEvent.id !== event.id && conflictingEvent.approvalStatus !== 'denied'))
    }

    async function getTeamScheduleFromMember(isCurrent) {
        const memberId = event.member.userId
        const memberDetails = await getMemberDetails(memberId)
        if (!isCurrent()) return
        const primaryTeams = (memberDetails.teams || []).filter(team => !team.isSecondary)
        setMemberTeams(primaryTeams)
        const associatedTeamIds = primaryTeams.map((team) => team.id)
        if (!associatedTeamIds) {
            return
        }
        for (const associatedTeamId of associatedTeamIds) {
            getConflictingEvents(associatedTeamId)
        }
    }

    useEffect(() => {
        let current = true
        if (event.member?.userId && String(event.id) === String(id)) {
            getTeamScheduleFromMember(() => current)
        }
        return () => { current = false }
    }, [initialEvent, id])

    useEffect(() => {
        getEvent()
    }, [id])

    const approvalOptions = [
        {
            key: 'pending',
            value: 'pending',
            text: 'Pending'
        },
        {
            key: 'approved',
            value: 'approved',
            text: 'Approved'
        },
        {
            key: 'denied',
            value: 'denied',
            text: 'Denied'
        },
    ]

    const disabledStyle = {
        opacity: "1"
    }

    const username = getUsername()
    const canApprove = !isReadOnly() && event.approvalStatus === 'pending' && (
        username === (event.designatedApprover?.userId || event.designatedApprover?.id)
        || event.previousApprovers?.some(approver => (approver.userId || approver.id) === username)
        || (event.member?.userId !== username && canApproveTeam(memberTeams))
    )

    if(!isAssociateManagerOrAbove() && event.member && event.member.userId !== getUsername()) {
        return <Unauthorized />
    }

    return <LoadingWrapper loading={loading} text="Loading event">
        <div id="centered-form" align="center">
            {notFoundError && <Message negative>This event does not exist. Perhaps it was deleted?</Message>}
            {genericError && <Message negative>Something went wrong when getting this event.</Message>}
            {approvalError && <Message negative role="alert">{approvalError}</Message>}
            <Form style={{ textAlign: "left", width: "35%", minWidth: "20rem" }}>
                <FormGroup>
                    <FormField width={8}>
                        <label>Title</label>
                        <Form.Input value={event.title} className="icct-disabled-input" />
                    </FormField>
                    <FormField
                        className="icct-disabled-input"
                        width={8}
                        label={() => {
                            return <><label>Member <span><Link to={"/members/" + event.member?.userId + "/view"}>(View member)</Link></span></label></>
                        }}
                        control={Input}
                        selection
                        value={event.member?.name ?? ''}
                    // onChange={(e, data) => handleChange('member', data.value)}
                    />
                </FormGroup>
                <FormGroup>
                    <FormField width={8}>
                        <label>Start</label>
                        <Form.Input type='datetime-local' value={event.start} className="icct-disabled-input" />
                    </FormField>
                    <FormField width={8}>
                        <label>End</label>
                        <Form.Input type='datetime-local' value={event.end} className="icct-disabled-input" />
                    </FormField>
                </FormGroup>
                <FormGroup>
                    <FormField
                        className="icct-disabled-selection"
                        width={8}
                        label={"Type"}
                        control={Dropdown}
                        selection
                        options={eventTypes}
                        value={event.type}
                    // onChange={(e, data) => handleChange('type', data.value)}
                    />
                    <FormField
                        className="icct-disabled-selection"
                        width={8}
                        label={() => <label style={disabledStyle}>Approval Status</label>}
                        control={Dropdown}
                        selection
                        options={approvalOptions}
                        value={event.approvalStatus}
                    // onChange={(e, data) => handleChange('approvalStatus', data.value)}
                    // disabled
                    />
                </FormGroup>
                {event.approvedBy && <FormGroup>
                    <FormField width={8}>
                        <label>{event.approvalStatus === 'approved' ? 'Approved' : 'Denied'} by</label>
                        <Form.Input value={event.approvedBy?.name} className="icct-disabled-input" />
                    </FormField>
                    <FormField width={8}>
                        <label>{event.approvalStatus === 'approved' ? 'Approved' : 'Denied'} on</label>
                        <Form.Input
                            className="icct-disabled-input"
                            value={event.approvedOn ?
                                new Date(event.approvedOn).toLocaleDateString() + " " + new Date(event.approvedOn).toLocaleTimeString("en-US", { timeStyle: "short" })
                                : ''} />
                    </FormField>
                </FormGroup>}
                {!event.approvedBy && <FormGroup>
                    <FormField width={8}>
                        <label>Designated approver</label>
                        <Form.Input value={event.designatedApprover?.name} className="icct-disabled-input" />
                    </FormField>
                </FormGroup>}
                <FormField
                    control={TextArea}
                    width={16}
                    label="Notes"
                    value={event.notes ?? ''}
                    onChange={(e, data) => handleChange('notes', data.value)}
                    className="icct-disabled-input"
                />
                <FormField
                    id="absence-denial-reason"
                    error={denialReasonError}
                    control={Input}
                    width={16}
                    label="Reason for denial (if applicable)"
                    value={event.deniedReason ?? ''}
                    onChange={(e, data) => handleChange('deniedReason', data.value)}
                />
            </Form>
            {canApprove && <div style={{ margin: "1rem" }}>
                <Button disabled={loading} style={{ backgroundColor: "var(--add)", color: "white" }} onClick={approveEvent}>Approve</Button>
                <Button disabled={loading} style={{ backgroundColor: "var(--delete)", color: "white" }} onClick={denyEvent}>Deny</Button>
            </div>}
            <div style={{display: "flex", alignItems: "flex-start", flexDirection: "column"}}>
            {canApprove && conflictingEvents.length > 0 && conflictingEvents.map((conflictingEvent) => <div style={{ margin: ".5rem", fontWeight: "bold" }}>
                <Icon className="exclamation circle" color="red" />
                <Link to={'/members/' + conflictingEvent.member.userId + '/view'}>
                    {conflictingEvent.member.name}
                </Link> already has an <Link to={'/events/' + conflictingEvent.id}>absence</Link> ({statusMap[conflictingEvent.type] ?? conflictingEvent.type}) at this time.
            </div>)
            }
            </div>
            <div className="divider-bottom" />
            {canApprove && <div>
                <Icon className="exclamation circle" />
                Please review the <a rel="noreferrer" href="https://tdsinc.sharepoint.com/:b:/r/sites/TDS-EmployeeRelations/Shared%20Documents/Field%20Services%20Guidelines%20to%20Attendance.pdf?csf=1&web=1&e=1Hjvwz" style={{ fontWeight: "bold" }} target="_blank">
                    attendance guidelines
                </a> before approving or denying absences.</div>}


        </div>
    </LoadingWrapper>

}